import React from "react";

const PILLARS = [
  {
    n: "01",
    title: "Capture",
    body: "Turn traffic, DMs, QR scans, and website visits into clean lead records.",
    detail: ["Landing pages", "QR + bio links", "Source tagging"],
  },
  {
    n: "02",
    title: "Convert",
    body: "Use automated follow-up, booking flows, and payment links to move leads forward.",
    detail: ["Email + SMS sequences", "Booking + payment", "Pipeline routing"],
  },
  {
    n: "03",
    title: "Compound",
    body: "Use reviews, referrals, reactivation, and reporting to keep the system improving.",
    detail: ["Review automations", "Reactivation flows", "Source reporting"],
  },
];

export default function MethodSection({ tone = "light" }) {
  const dark = tone === "dark";
  return (
    <section
      data-testid="method-section"
      className="px-5 sm:px-6 md:px-10"
    >
      <div className="max-w-7xl mx-auto">
        <div className={`rounded-[24px] sm:rounded-[28px] px-6 sm:px-10 md:px-14 py-16 sm:py-20 md:py-24 lg:py-28 grain relative overflow-hidden ${
          dark ? "bg-[#151514]" : "bg-sand-200"
        }`}>
          {dark && (
            <div
              aria-hidden
              className="absolute inset-0 opacity-30 pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(640px 360px at 12% 18%, rgba(165,106,67,0.30), transparent 60%), radial-gradient(640px 360px at 88% 88%, rgba(177,116,75,0.18), transparent 60%)",
              }}
            />
          )}
          <div className="relative grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7 reveal">
              <p className={`eyebrow ${dark ? "!text-[#A56A43]" : ""}`}>Our Method</p>
              <h2 className={`font-display mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-[3.2rem] leading-[1.05] tracking-[-0.02em] font-semibold ${
                dark ? "text-[#F4F0E8]" : "text-ink"
              }`}>
                We build the system <span className={`italic ${dark ? "text-[#BEB8AF]" : "text-ink-muted"}`}>closest to revenue</span> first.
              </h2>
            </div>
            <div className="lg:col-span-5 reveal">
              <p className={`text-base md:text-lg leading-relaxed ${
                dark ? "text-[#BEB8AF]" : "text-ink-muted"
              }`}>
                Most businesses don't need more software. They need a cleaner
                path from interest to booked revenue. We identify where leads
                are leaking, then build the simplest system that moves prospects
                from first touch to follow-up, booking, payment, and review.
              </p>
            </div>
          </div>

          <div className="relative mt-12 sm:mt-14 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            {PILLARS.map((p, idx) => (
              <article
                key={p.title}
                data-testid={`method-pillar-${idx}`}
                className={`reveal relative rounded-2xl p-7 sm:p-8 md:p-9 hover:-translate-y-0.5 transition-transform border ${
                  dark
                    ? "bg-[#1A1A18] border-[#3C3935]"
                    : "bg-sand-50 border-sand-300"
                }`}
              >
                <div className="flex items-start justify-end">
                  <span className={`h-1.5 w-1.5 rounded-full ${dark ? "bg-[#B1744B]" : "bg-clay"}`} />
                </div>
                <h3 className={`font-display mt-7 text-2xl md:text-3xl font-semibold tracking-tight ${
                  dark ? "text-[#F4F0E8]" : "text-ink"
                }`}>
                  {p.title}
                </h3>
                <p className={`mt-3 text-[15px] leading-relaxed ${
                  dark ? "text-[#BEB8AF]" : "text-ink-muted"
                }`}>
                  {p.body}
                </p>
                <ul className="mt-7 space-y-1.5">
                  {p.detail.map((d) => (
                    <li
                      key={d}
                      className={`font-mono text-[11px] uppercase tracking-[0.28em] flex items-center gap-2 ${
                        dark ? "text-[#8E887F]" : "text-ink-muted"
                      }`}
                    >
                      <span className={`h-px w-3 ${dark ? "bg-[#33302D]" : "bg-sand-300"}`} />
                      {d}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
